import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CipherService } from './cipher.service';
import {
  TIPOS_DOCUMENTO_FALLBACK,
  TipoDocumento,
  TipoDocumentoOption,
  aOpcionesSelect,
  aOpcionesSelectPorId,
  filtrarTiposDocumentoPersonaNatural,
  normalizarTiposDocumento
} from '../utils/tipos-documento.util';

const CACHE_KEY = 'TIPOS_DOCUMENTO';
const UTILITARIO_PATH = 'CEB_TIPO_IDENTIFICACION';

export type TiposDocumentoFiltro = 'todos' | 'personaNatural';

/**
 * Catálogo maestro de tipos de identificación (mismo utilitario que compra/SSPS).
 * Cache en memoria + sessionStorage; fallback si el servicio no responde.
 */
@Injectable({ providedIn: 'root' })
export class TiposDocumentoService {
  private readonly base = environment.ordenPedidoServiceUrl;
  private memoryCache: TipoDocumento[] | null = null;
  private inflight$: Observable<TipoDocumento[]> | null = null;
  private readonly listado$ = new BehaviorSubject<TipoDocumento[]>(TIPOS_DOCUMENTO_FALLBACK);

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {
    const stored = this.readSession();
    if (stored?.length) {
      this.memoryCache = stored;
      this.listado$.next(stored);
    }
  }

  /** Snapshot reactivo del catálogo (fallback hasta que cargue). */
  get tipos$(): Observable<TipoDocumento[]> {
    return this.listado$.asObservable();
  }

  get snapshot(): TipoDocumento[] {
    return this.listado$.value;
  }

  /**
   * Precarga el catálogo (idempotente). Llamar al entrar al shell o a Gestión/Admin.
   */
  ensureLoaded(): Observable<TipoDocumento[]> {
    return this.cargar();
  }

  /** Fuerza recarga desde backend (o fallback). */
  recargar(): Observable<TipoDocumento[]> {
    this.memoryCache = null;
    this.inflight$ = null;
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {
      /* ignore */
    }
    return this.cargar();
  }

  opcionesSelect(filtro: TiposDocumentoFiltro = 'todos'): TipoDocumentoOption[] {
    return aOpcionesSelect(this.aplicarFiltro(this.snapshot, filtro));
  }

  opcionesSelectPorId(filtro: TiposDocumentoFiltro = 'todos'): TipoDocumentoOption[] {
    return aOpcionesSelectPorId(this.aplicarFiltro(this.snapshot, filtro));
  }

  opcionesSelect$(filtro: TiposDocumentoFiltro = 'todos'): Observable<TipoDocumentoOption[]> {
    return this.ensureLoaded().pipe(map((tipos) => aOpcionesSelect(this.aplicarFiltro(tipos, filtro))));
  }

  opcionesSelectPorId$(filtro: TiposDocumentoFiltro = 'todos'): Observable<TipoDocumentoOption[]> {
    return this.ensureLoaded().pipe(
      map((tipos) => aOpcionesSelectPorId(this.aplicarFiltro(tipos, filtro)))
    );
  }

  private cargar(): Observable<TipoDocumento[]> {
    if (this.memoryCache?.length) {
      return of(this.memoryCache);
    }
    if (this.inflight$) {
      return this.inflight$;
    }

    this.inflight$ = this.fetchDesdeUtilitario().pipe(
      map((raw) => normalizarTiposDocumento(raw)),
      map((tipos) => (tipos.length ? tipos : TIPOS_DOCUMENTO_FALLBACK)),
      catchError(() => of(TIPOS_DOCUMENTO_FALLBACK)),
      tap((tipos) => {
        this.memoryCache = tipos;
        this.writeSession(tipos);
        this.listado$.next(tipos);
        this.inflight$ = null;
      }),
      shareReplay(1)
    );
    return this.inflight$;
  }

  /**
   * POST {ordenPedido}/utilitario — body `{ data: encrypt(JSON.stringify(path)) }`
   * igual que Canal_Ventas `consultarUtilitario`.
   */
  private fetchDesdeUtilitario(): Observable<unknown> {
    const url = `${this.base}/utilitario`;
    const headers = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    const pathPayload = JSON.stringify(UTILITARIO_PATH);

    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<unknown>(url, { data: pathPayload }, headers);
    }

    return from(this.cipher.encrypt(pathPayload)).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | unknown>(url, { data: encrypted }, headers)
      ),
      switchMap((response) => this.unwrap(response))
    );
  }

  private unwrap(response: { data?: unknown } | unknown): Observable<unknown> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => {
          const parsed = JSON.parse(plain);
          if (parsed && Array.isArray(parsed.data)) {
            return parsed.data;
          }
          return parsed;
        })
      );
    }
    if (wrapped?.data && Array.isArray(wrapped.data)) {
      return of(wrapped.data);
    }
    return of(response);
  }

  private aplicarFiltro(tipos: TipoDocumento[], filtro: TiposDocumentoFiltro): TipoDocumento[] {
    if (filtro === 'personaNatural') {
      return filtrarTiposDocumentoPersonaNatural(tipos);
    }
    return tipos;
  }

  private readSession(): TipoDocumento[] | null {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = normalizarTiposDocumento(JSON.parse(raw));
      return parsed.length ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeSession(tipos: TipoDocumento[]): void {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(tipos));
    } catch {
      /* ignore quota */
    }
  }
}
